import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { FileText, Edit, Mail, Download, User, MapPin, Calendar, AlertCircle, CheckCircle } from 'lucide-react';
import { fmtDate, fmtDateTime } from '@/lib/dates';

// Reformat any embedded ISO date (YYYY-MM-DD) inside a label to MM/DD/YY (display only).
const fmtLabelDates = (s) => (s || '').replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, mo, d) => `${mo}/${d}/${y.slice(2)}`);
import { toast } from 'sonner';

const WorkOrderDetail = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workOrderId = searchParams.get('id');
  
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
      fetchUsers();
    }
  }, [workOrderId, token]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/work-orders/${workOrderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkOrder(response.data);
      setNewStatus(response.data.status || 'Pending');
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      toast.error('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      const selectedUser = users.find(u => u.id === selectedUserId);
      await axios.put(
        `${API}/work-orders/${workOrderId}`,
        {
          assigned_to: selectedUser.name,
          assigned_to_id: selectedUserId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User assigned successfully');
      setShowAssignDialog(false);
      fetchWorkOrder();
    } catch (error) {
      console.error('Failed to assign user:', error);
      toast.error('Failed to assign user');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(
        `${API}/work-orders/${workOrderId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      setShowStatusDialog(false);
      fetchWorkOrder();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'P1': 'bg-red-100 text-red-800 border-red-300',
      'P2': 'bg-orange-100 text-orange-800 border-orange-300',
      'P3': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'P4': 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-gray-100 text-gray-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div>
        
        <div className="flex justify-center items-center h-64">Loading work order...</div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div>
        
        <div className="p-4 text-center">
          <p className="text-gray-600">Work order not found</p>
          <Button onClick={() => navigate('/admin/work-orders')} className="mt-4">
            Back to Work Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        {/* Header Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/work-orders')}>
                <FileText size={16} className="mr-2" />
                Back to List
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/admin/work-orders/edit?id=${workOrderId}`)}>
                <Edit size={16} className="mr-2" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAssignDialog(true)}>
                <User size={16} className="mr-2" />
                Assign User
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowStatusDialog(true)}>
                <CheckCircle size={16} className="mr-2" />
                Update Status
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Download size={16} className="mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" size="sm" disabled>
                <Mail size={16} className="mr-2" />
                Send Email
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Work Order Overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Work Order Information</CardTitle>
              <div className="flex gap-2">
                <Badge className={getPriorityColor(workOrder.priority_level)}>
                  {workOrder.priority_level}
                </Badge>
                <Badge className={getStatusColor(workOrder.status)}>
                  {workOrder.status || 'Pending'}
                </Badge>
                {workOrder.is_billable && (
                  <Badge className="bg-green-100 text-green-800">Billable</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600">Work Order Number</Label>
                  <p className="font-medium">{workOrder.tfs_work_order_number}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Customer</Label>
                  <p className="font-medium">{workOrder.customer_name}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Service Location</Label>
                  <p className="font-medium">{workOrder.service_location}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Trade Type</Label>
                  <p className="font-medium">{workOrder.trade_type}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Job Name</Label>
                  <p className="font-medium">{workOrder.job_name ? fmtLabelDates(workOrder.job_name) : 'N/A'}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <Label className="text-sm text-gray-600">Date Received</Label>
                  <p className="font-medium">
                    {fmtDateTime(workOrder.date_received)}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Required Service Date</Label>
                  <p className="font-medium">
                    {fmtDate(workOrder.required_service_date)}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Received By</Label>
                  <p className="font-medium">{workOrder.work_order_received_by || 'N/A'}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Assigned To</Label>
                  <p className="font-medium">{workOrder.assigned_to || 'Unassigned'}</p>
                </div>
                
                <div>
                  <Label className="text-sm text-gray-600">Source</Label>
                  <p className="font-medium">{workOrder.source || 'N/A'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Work Description */}
        <Card>
          <CardHeader>
            <CardTitle>Work Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">
              {workOrder.description_of_work || 'No description provided'}
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Main Contact Name</Label>
                <p className="font-medium">{workOrder.main_contact_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Main Contact Phone</Label>
                <p className="font-medium">{workOrder.main_contact_phone || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Main Contact Email</Label>
                <p className="font-medium">{workOrder.main_contact_email || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Internal Notes */}
        {workOrder.internal_notes && (
          <Card>
            <CardHeader>
              <CardTitle>Internal Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap">{workOrder.internal_notes}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Assign User Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Work Order to User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="user-select">Select Field User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignUser} className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800">
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Work Order Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="status-select">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800">
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderDetail;
